import { LOCATION_INITIALIZED } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from "@angular/core";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import {
  MatMomentDateModule,
  provideMomentDateAdapter,
} from "@angular/material-moment-adapter";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { PaginatorHelper } from "@core/helpers/paginator-helper";
import { RegisterInterceptor } from "@core/interceptors/register.interceptor";
import { AuthEffects } from "@features/auth/store/auth.effects";
import { ClientsAndAccountsEffects } from "@features/clients-and-accounts/store/clients-and-accounts.effects";
import { CompositionModelEffects } from "@features/composition-model-management/store/composition-model.effects";
import { CorporateActionsEffects } from "@features/corporate-actions/store/corporate-actions.effects";
import { DashboardEffects } from "@features/dashboard/store/dashboard.effects";
import { ManagementEffects } from "@features/management/store/management.effects";
import { MessageEffects } from "@features/messages/store/message.effects";
import { ProductEffects } from "@features/products/store/product.effects";
import { ReportsEffects } from "@features/reports-panel/store/reports.effects";
import { SystemTimeEffects } from "@features/system-time/store/system-time.effects";
import { TradingEffects } from "@features/trading/store/trading.effects";
import { TransactionEffects } from "@features/transactions/store/transaction.effects";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { NgxCleaveDirectiveModule } from "ngx-cleave-directive";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";

import * as fromApp from "./store/app.reducer";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";

@NgModule({
  declarations: [AppComponent],
  imports: [
    NgxSkeletonLoaderModule.forRoot({
      theme: {
        extendsFromRoot: true,
        height: "5vh",
        margin: 0,
        padding: 0,
      },
    }),
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatMomentDateModule,
    MatSnackBarModule,
    NgxCleaveDirectiveModule,
    StoreModule.forRoot(fromApp.APP_REDUCER),
    EffectsModule.forRoot([
      AuthEffects,
      DashboardEffects,
      ManagementEffects,
      TradingEffects,
      ProductEffects,
      MessageEffects,
      ClientsAndAccountsEffects,
      TransactionEffects,
      ReportsEffects,
      CorporateActionsEffects,
      CompositionModelEffects,
      SystemTimeEffects,
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient): MultiTranslateHttpLoader => {
          return new MultiTranslateHttpLoader(http, [
            {
              prefix: "./assets/i18n/features/accountmanager/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/advisor/", suffix: ".json" },
            { prefix: "./assets/i18n/features/auth/", suffix: ".json" },
            { prefix: "./assets/i18n/features/bankbatches/", suffix: ".json" },
            { prefix: "./assets/i18n/features/client/", suffix: ".json" },
            {
              prefix: "./assets/i18n/features/collectionmanagement/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/common/", suffix: ".json" },
            {
              prefix: "./assets/i18n/features/communication/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/dashboard/", suffix: ".json" },
            { prefix: "./assets/i18n/features/dividend/", suffix: ".json" },
            { prefix: "./assets/i18n/features/rebalance/", suffix: ".json" },
            { prefix: "./assets/i18n/features/disbursement/", suffix: ".json" },
            {
              prefix: "./assets/i18n/features/financialinstitute/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/funds/", suffix: ".json" },
            { prefix: "./assets/i18n/features/insurer/", suffix: ".json" },
            { prefix: "./assets/i18n/features/intermediary/", suffix: ".json" },
            { prefix: "./assets/i18n/features/messages/", suffix: ".json" },
            {
              prefix: "./assets/i18n/features/productmanagement/",
              suffix: ".json",
            },
            {
              prefix: "./assets/i18n/features/composition-management/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/report/", suffix: ".json" },
            { prefix: "./assets/i18n/features/shared/", suffix: ".json" },
            { prefix: "./assets/i18n/features/trading/", suffix: ".json" },
            {
              prefix: "./assets/i18n/features/clients-and-accounts/",
              suffix: ".json",
            },
            { prefix: "./assets/i18n/features/notfound/", suffix: ".json" },
          ]);
        },
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService, injector: Injector): object => {
        return async (): Promise<void> => {
          await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

          // this language will be used as a fallback when a translation
          translate.setDefaultLang("nl");
          try {
            // for now, we only need nl as a default language.(this might change in the future)
            await translate.use(translate.defaultLang).toPromise();
          } catch (err) {
            console.error(err);
          }
        };
      },
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "DD-MM-YYYY",
        },
        display: {
          dateInput: "DD-MM-YYYY",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY",
        },
      },
    },
    provideMomentDateAdapter(),
    {
      provide: LOCALE_ID,
      useValue: "nl-NL",
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RegisterInterceptor,
      multi: true,
    },
    {
      provide: "XSRF_COOKIE_NAME",
      useValue: "XSRF-TOKEN",
    },
    {
      provide: "XSRF_HEADER_NAME",
      useValue: "X-XSRF-TOKEN",
    },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorHelper,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: "dialog", restoreFocus: true },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
